import type { Block } from "building-types/blocks/blockTypes";
import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { type DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { type DatumUsage, DatumVerticalUsageType } from "building-types/datums/datumUsageTypes";
import type { Facade, FacadeSpanStratum, FacadeSweepStratum } from "building-types/facades/facadeTypes";
import type { Label } from "building-types/facades/label";
import type { SheathingComponentEntity } from "building-types/floors/sheathingComponentTypes";
import type { SlabComponentEntity } from "building-types/floors/slabComponentTypes";
import type { FoundationAssemblyEntity } from "building-types/foundations/foundationAssemblyEntityTypes";
import type { FoundationComponentEntity } from "building-types/foundations/foundationComponentEntityTypes";
import type { FramingComponentEntity } from "building-types/framing/framingComponentTypes";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { WallOpening } from "building-types/openings/wallOpeningTypes";
import type { HalfEdgeAssociationTable } from "building-types/planarGraphs/planarGraphAssociationTypes";
import {
  DEFAULT_CORNER_TRIM_PROFILE_DESCRIPTOR,
  DEFAULT_TRIM_PROFILE_DESCRIPTOR,
} from "building-types/profiles/defaultBoardTrimProfiles";
import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import type {
  CornerBoardDatumsAABB,
  GeneratedRoof,
  GeneratedRoofSystem,
  UnderRoofTrim,
} from "building-types/roofs/generatedRoofSystemTypes";
import type { MultiGableRoofClipContext } from "building-types/roofs/multiGableRoofClipContextTypes";
import type { RoofPerimeterRail } from "building-types/roofs/roofPerimeterTypes";
import { RoofRailCornerType } from "building-types/roofs/roofPerimeterTypes";
import { isRoofTrimRailTypeSoffit } from "building-types/roofs/roofTrimTypes";
import type { RoofWallSystem } from "building-types/roofs/roofWallSystemTypes";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import {
  BuildingGenerationIssueType,
  type BuildingGenerationIssue,
} from "building-types/settings/buildingGenerationIssue";
import type { ExteriorTrimSettings } from "building-types/settings/exteriorTrimSettings";
import type { FoundationSettings } from "building-types/settings/foundationSettings";
import type { ActiveProfileSource } from "building-types/settings/profileCatalogSettings";
import type { RoofFaces } from "building-types/settings/roofTypes";
import { type RoofSystemLevel } from "building-types/settings/roofTypes";
import {
  CornerBoardTrimAlignment,
  EaveType,
  ExteriorWallOffsetMode,
  RoofWallMode,
} from "building-types/settings/settingsTypesEnum";
import type { WallCatalogSettings } from "building-types/settings/wallCatalogSettings";
import type { WallSettings } from "building-types/settings/wallSettings";
import { ValidCornerCondition } from "building-types/trim/cornerBoardTypes";
import { WallType } from "building-types/walls/wallEnums";
import type { WallSideGeneratedDatums } from "building-types/walls/wallSideGeneratedDatums";
import type { WallMassSystem, WallSystem } from "building-types/walls/wallSytemTypes";
import { GABLE_ROOF_THICKNESS } from "common/constants/buildingObjects";
import type { RWTable, Table } from "common/data/table";
import { levelInchesNew } from "common/utils/nominalNumbers";
import { ENTITY_ID_SEPARATOR } from "doc/entity/componentInstanceEntityResolve";
import { docEntityGetSortedChildrenFromTable } from "doc/entity/docEntityGetChildrenFromTable";
import { hasEaveSoffit, isOpenEave } from "doc/fasciaTopTrimSizeToValue";
import { isSideRoofFace } from "doc/utils/roofTools";
import { aabb2New } from "geom/aabb2Types";
import { aabb2Height } from "geom/aabb2Types";
import type { AABB2 } from "geom/aabb2Types";
import type { NodeBSP } from "geom/bspTypes";
import { DIST_EPSILON, DIST_EPSILON_SQUARED, Line2Side } from "geom/core";
import { line2Side } from "geom/line2";
import { polyLine2MiterPt } from "geom/offset";
import type { PlanarGraph } from "geom/planarGraph";
import { planarGraphHalfEdgeEnd, planarGraphTracePerimeter } from "geom/planarGraph";
import { polyLine2OnPlane } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polygonPlanarGraphNew } from "geom/polygonPlanarGraph";
import { polyLine2AABB2, polyLine2Copy, polyLine2Simplify } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { polyLine3EndPoint, polyLine3Length, polyLine3StartPoint } from "geom/polyLine3";
import type { Vec2 } from "geom/vec2";
import { vec3DistSquared } from "geom/vec3";
import type { Vec3 } from "geom/vec3";
import RBush from "rbush";
import { pushMissingProfileReferenceIssue } from "../../elements/profileMissingReference";
import { getActiveProfileDescriptor, getProfileExtrusionDescriptorProfile } from "../../elements/profileTools";
import { getEaveDepth, hasValidFriezeBoards } from "../../elements/roofTrimUtility";
import { wallAssemblyVerticalDatumFromFacade } from "../../elements/wallAssemblies/wallAssemblyDefaults";
import { makeWallPathsForLevel } from "../../generation/generateWallPaths";
import { collectWallFacadeDatums } from "../../utils/facade";
import { getRoofPlanarGraphPerimeterInfo, makePlanarGraphLevelAssociations } from "../../utils/planarGraphPerimeters";
import {
  CORNER_BOARD_COLLISION_TOLERANCE,
  cornerBoardConfigForEdge,
  emptyCornerBoardConfig,
  getCornerCondition,
  isValidCornerCondition,
  ROOF_CORNER_BOARD_TOLERANCE,
} from "../cornerBoards/cornerBoardTools";
import { WallDatumAbstractionProvider } from "../datums/datumAbstractionProviders";
import { getDatumAbstraction } from "../datums/datumAbstractions";
import { defaultWallDatumAbstractions } from "../datums/defaultDatumAbstractions";
import { includeElementBasedOnOrientation } from "../multiGableRoofTools";
import { getInsideCornerHalfEdges } from "../walls/wallInsideCorners";
import { makeWallMassSystem, makeWallSystem } from "../walls/wallSystem";
import { makeRoofHeelHeightOffsets } from "./roofHeelHeightOffsets";
import { buildRoofPerimeterRailsRTree, getRoofHeightAtPoint, getRoofPerimeterWallRail } from "./roofPerimeterRails";
import { buildEnclosingRail } from "./roofTrimRails";
import { adjustRoofRailsExteriorFinishOffset } from "./roofTrimRailsTools";
import { buildWallOverlapBSPs, getUpperFloorsRoomsBSPs } from "./roofWalls";
import { clipRoofWallSystem } from "./roofWallSystemClipping";
import {
  makeRoofWallSystemPerimeters,
  makeRoofWallLevel,
  makeRoofBlock,
  makeRoofRooms,
  makeRoofLabelHalfEdgeMaps,
  makeRoofWallCutHalfEdgeMap,
  makeRoofRoomsConnections,
  makeRoofWallSystemEdgeMap,
} from "./roofWallSystemVirtualLevel";

interface MakeRoofWallSystemsOptions {
  readonly levels: Table<Level>;
  readonly docRooms: Table<StaticRoom>;
  readonly walls: Table<WallSystem>;
  readonly blocks: Table<Block>;
  readonly roofSystem: GeneratedRoofSystem;
  readonly wallSettings: WallSettings;
  readonly wallCatalogSettings: WallCatalogSettings;
  readonly facades: Table<Facade>;
  readonly datumAbstractions: RWTable<DatumAbstraction>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly slabComponents: Table<SlabComponentEntity>;
  readonly sheathingComponents: Table<SheathingComponentEntity>;
  readonly foundationSettings: FoundationSettings;
  readonly foundationComponents: Table<FoundationComponentEntity>;
  readonly foundationAssemblies: Table<FoundationAssemblyEntity>;
  readonly profileSource: ActiveProfileSource;
  readonly exteriorTrimSettings: ExteriorTrimSettings;
  readonly facadeSpanStratum: Table<FacadeSpanStratum>;
  readonly facadeSweepStratum: Table<FacadeSweepStratum>;
  readonly roofWallCuts: WallOpening[];
  readonly roofWallDiscardBSPs: Table<NodeBSP>;
  readonly roofSystemLevels: Table<RoofSystemLevel>;
  readonly mirror: boolean;
  readonly issues: BuildingGenerationIssue[];
}

export function makeRoofWallSystems({
  levels,
  docRooms,
  walls,
  blocks,
  roofSystem,
  wallSettings,
  wallCatalogSettings,
  facades,
  datumAbstractions,
  framingComponents,
  slabComponents,
  sheathingComponents,
  foundationSettings,
  foundationComponents,
  foundationAssemblies,
  profileSource,
  exteriorTrimSettings,
  facadeSpanStratum,
  facadeSweepStratum,
  roofWallCuts,
  roofWallDiscardBSPs,
  roofSystemLevels,
  mirror,
  issues,
}: MakeRoofWallSystemsOptions): GeneratedRoofSystem {
  const { roofs, clipContext } = roofSystem;
  const { insideBSP } = clipContext;

  const wallOverlapBSPs = buildWallOverlapBSPs(walls, blocks, insideBSP, false);

  const newSubRoofs: RWTable<GeneratedRoof> = {};

  for (const subRoofId in roofs) {
    const roof = roofs[subRoofId];
    const { levelId, wallFaces, wallMaterial, isDormer } = roof;
    const { datumsAtCorner, underRoofTrim } = wallFaces;

    // TODO: When we generate the proper wall system for the dormer walls misalignment won't be a problem.
    // At that point, we should remove the dormer check.
    if (isDormer) {
      newSubRoofs[subRoofId] = roof;
      continue;
    }

    const roofWallSettings: WallSettings = {
      ...wallSettings,
      exteriorWallOffsetMode: ExteriorWallOffsetMode.INSIDE_THICKENING_ALL,
      exteriorWallMaterial: wallMaterial ?? wallSettings.exteriorWallMaterial,
    };

    const level = levels[levelId];
    const roofWallDiscardBSP = roofWallDiscardBSPs[roof.levelId];
    const upperFloorsRoomsBSPs = getUpperFloorsRoomsBSPs(levels, levels[levelId], roofSystemLevels);
    const { halfStoriesBSP } = roofSystemLevels[levelId];

    const roofWallSystem = makeRoofWallSystem({
      level,
      docRooms,
      roof,
      roofs,
      wallSettings: roofWallSettings,
      wallCatalogSettings,
      facades,
      mutableDatumAbstractionsOut: datumAbstractions,
      framingComponents,
      slabComponents,
      sheathingComponents,
      foundationSettings,
      foundationComponents,
      foundationAssemblies,
      profileSource,
      exteriorTrimSettings,
      facadeSpanStratum,
      facadeSweepStratum,
      roofWallCuts,
      clipContext,
      roofWallDiscardBSP,
      wallOverlapBSPs,
      upperFloorsRoomsBSPs,
      halfStoriesBSP,
      datumsAtCorner,
      underRoofTrim,
      mirror,
      issues,
    });

    const roofWithWallSystem = {
      ...roof,
      roofWallSystem: roofWallSystem ?? undefined,
    };

    newSubRoofs[subRoofId] = adjustRoofRailsExteriorFinishOffset(roofWithWallSystem, wallSettings);
  }

  return {
    ...roofSystem,
    roofs: newSubRoofs,
  };
}

interface MakeRoofWallSystemOptions {
  readonly level: Level;
  readonly docRooms: Table<StaticRoom>;
  readonly roof: GeneratedRoof;
  readonly roofs: RWTable<GeneratedRoof>;
  readonly wallSettings: WallSettings;
  readonly wallCatalogSettings: WallCatalogSettings;
  readonly facades: Table<Facade>;
  /**
   * @NOTE - This is the final table of datum abstractions that should be used for the rest of the generation process.
   * This table must be updated every time a roof wall system is generated.
   */
  readonly mutableDatumAbstractionsOut: RWTable<DatumAbstraction>;
  readonly framingComponents: Table<FramingComponentEntity>;
  readonly slabComponents: Table<SlabComponentEntity>;
  readonly sheathingComponents: Table<SheathingComponentEntity>;
  readonly foundationSettings: FoundationSettings;
  readonly foundationComponents: Table<FoundationComponentEntity>;
  readonly foundationAssemblies: Table<FoundationAssemblyEntity>;
  readonly profileSource: ActiveProfileSource;
  readonly exteriorTrimSettings: ExteriorTrimSettings;
  readonly facadeSpanStratum: Table<FacadeSpanStratum>;
  readonly facadeSweepStratum: Table<FacadeSweepStratum>;
  readonly roofWallCuts: WallOpening[];
  readonly clipContext: MultiGableRoofClipContext;
  readonly roofWallDiscardBSP: NodeBSP;
  readonly wallOverlapBSPs: NodeBSP[];
  readonly upperFloorsRoomsBSPs: NodeBSP[];
  readonly halfStoriesBSP: NodeBSP | null;
  readonly datumsAtCorner: RBush<CornerBoardDatumsAABB>;
  readonly underRoofTrim: UnderRoofTrim[];
  readonly mirror: boolean;
  readonly issues: BuildingGenerationIssue[];
}

export function makeRoofWallSystem({
  level,
  docRooms,
  roof,
  roofs,
  wallSettings,
  wallCatalogSettings,
  facades,
  mutableDatumAbstractionsOut,
  framingComponents,
  slabComponents,
  sheathingComponents,
  foundationSettings,
  foundationComponents,
  foundationAssemblies,
  profileSource,
  exteriorTrimSettings,
  facadeSpanStratum,
  facadeSweepStratum,
  roofWallCuts,
  clipContext,
  roofWallDiscardBSP,
  wallOverlapBSPs,
  upperFloorsRoomsBSPs,
  halfStoriesBSP,
  datumsAtCorner,
  underRoofTrim,
  mirror,
  issues,
}: MakeRoofWallSystemOptions): RoofWallSystem | null {
  const { roofVolume, gableEndLabels, roofSettings, suppressWallExtension, legacyRoof } = roof;
  const rootInstanceId = roof.componentInstanceChain?.[0];
  const resolveFacadeId = (rawFacadeId: string): string =>
    rootInstanceId !== undefined ? `${rawFacadeId}${ENTITY_ID_SEPARATOR}${rootInstanceId}` : rawFacadeId;
  const { peakHeight, baseHeight, perimeters } = roofVolume;
  const { peakDirection } = perimeters;
  const { roofWallMode = RoofWallMode.INTERIOR } = roofSettings;

  const eaveDepth = getEaveDepth(roofSettings);
  const loweredRoof = roofVolume.heelHeightOffsets.refOffset < eaveDepth - DIST_EPSILON;
  const heelHeightOffsets =
    suppressWallExtension && loweredRoof ? makeRoofHeelHeightOffsets(eaveDepth) : roofVolume.heelHeightOffsets;
  const facadeTrimOffsetZ = suppressWallExtension && loweredRoof ? heelHeightOffsets.refOffset : 0;
  const negativeHeelHeight = Math.min(heelHeightOffsets.refOffset + eaveDepth, 0);

  let fillGaps = false;
  let baseHeelHeight = Math.min(eaveDepth, Math.max(heelHeightOffsets.refOffset, 0));
  let floorElevation = baseHeight - baseHeelHeight + negativeHeelHeight;

  if (!suppressWallExtension) {
    fillGaps = Object.keys(roofs).length > 1 && baseHeight > level.floorElevation && !!halfStoriesBSP;
    baseHeelHeight = Math.max(heelHeightOffsets.refOffset, 0);
    floorElevation = fillGaps ? level.floorElevation : baseHeight - baseHeelHeight + negativeHeelHeight;
  }

  floorElevation = Math.max(level.floorElevation, floorElevation);

  function addIssuesForInvalidRoofSpanStrata(
    spanStrata: FacadeSpanStratum,
    face: RoofFaces,
    archetypeFacadeIdForIssue: string,
  ) {
    const { upperDatumUsage, lowerDatumUsage } = spanStrata;
    const lookupFacadeId = spanStrata.parentId ?? "";

    const upperDatumAbstraction = getDatumAbstraction({
      datumAbstractions: mutableDatumAbstractionsOut,
      datumUsage: upperDatumUsage,
      requestedUsageType: DatumVerticalUsageType.WALL_VERTICAL,
    });
    const lowerDatumAbstraction = getDatumAbstraction({
      datumAbstractions: mutableDatumAbstractionsOut,
      datumUsage: lowerDatumUsage,
      requestedUsageType: DatumVerticalUsageType.WALL_VERTICAL,
    });

    let issueUpperDatumWarning =
      upperDatumAbstraction &&
      upperDatumAbstraction.isValidForUsage(DatumVerticalUsageType.WALL_VERTICAL) &&
      !upperDatumAbstraction.isRoofDatum();
    const issueLowerDatumWarning =
      lowerDatumAbstraction &&
      lowerDatumAbstraction.isValidForUsage(DatumVerticalUsageType.WALL_VERTICAL) &&
      !lowerDatumAbstraction.isRoofDatum();

    if (isSideRoofFace[face]) {
      issueUpperDatumWarning = false;
    }

    if (!upperDatumAbstraction) {
      const fac = facades[lookupFacadeId];
      if (fac) {
        issues.push({
          type: BuildingGenerationIssueType.INVALID_DATUM,
          handle: { type: "FACADE", id: archetypeFacadeIdForIssue },
          message: `Roof "${roof.name}" has a Facade "${fac.name}" with an invalid upper datum "${upperDatumUsage.entityId}" at Facade Span "${spanStrata.name}"`,
        });
      }
    }
    if (!lowerDatumAbstraction) {
      const fac = facades[lookupFacadeId];
      if (fac) {
        issues.push({
          type: BuildingGenerationIssueType.INVALID_DATUM,
          handle: { type: "FACADE", id: archetypeFacadeIdForIssue },
          message: `Roof "${roof.name}" has a Facade "${fac.name}" with an invalid lower datum "${lowerDatumUsage.entityId}" at Facade Span "${spanStrata.name}"`,
        });
      }
    }
    if (issueUpperDatumWarning) {
      const fac = facades[lookupFacadeId];
      if (fac) {
        issues.push({
          type: BuildingGenerationIssueType.INVALID_DATUM,
          handle: { type: "FACADE", id: archetypeFacadeIdForIssue },
          message: `Roof "${roof.name}" has a Facade "${
            fac.name
          }" with an invalid upper datum "${upperDatumAbstraction?.getName()}" at Facade Span "${spanStrata.name}"`,
        });
      }
    }
    if (issueLowerDatumWarning) {
      const fac = facades[lookupFacadeId];
      if (fac) {
        issues.push({
          type: BuildingGenerationIssueType.INVALID_DATUM,
          handle: { type: "FACADE", id: archetypeFacadeIdForIssue },
          message: `Roof "${roof.name}" has a Facade "${
            fac.name
          }" with an invalid lower datum "${lowerDatumAbstraction.getName()}" at Facade Span "${spanStrata.name}"`,
        });
      }
    }
  }

  function isSpanStrataDatumValidForRoof(
    datumUsage: DatumUsage<DatumVerticalUsageType.WALL_VERTICAL>,
    face: RoofFaces,
  ): boolean {
    const strataDatumAbstraction = getDatumAbstraction({
      datumAbstractions: mutableDatumAbstractionsOut,
      datumUsage,
      requestedUsageType: DatumVerticalUsageType.WALL_VERTICAL,
    });

    //don't check datum validity for left and right faces
    if (!strataDatumAbstraction || isSideRoofFace[face]) {
      return true;
    }

    if (!strataDatumAbstraction.isValidForUsage(DatumVerticalUsageType.WALL_VERTICAL)) {
      return false;
    }

    if (legacyRoof && fillGaps) {
      return strataDatumAbstraction.isRoofDatum() || Math.abs(strataDatumAbstraction.getOffset()) < DIST_EPSILON;
    }

    return strataDatumAbstraction.isRoofDatum();
  }

  // create wall perimeter
  const roofWallPerimeters = makeRoofWallSystemPerimeters(roofVolume);

  if (roofWallPerimeters.length === 0) {
    return null;
  }

  // create a dummy level for the roof walls
  const wallTop = Math.max(baseHeight - baseHeelHeight - floorElevation, DIST_EPSILON);
  const roofBottom = wallTop;
  const roofTop = Math.max(peakHeight - floorElevation, roofBottom);
  const roofLevel = makeRoofWallLevel(floorElevation, wallTop, roofBottom, roofTop);
  const roofLevels = { [roofLevel.id]: roofLevel };
  const roofLevelAttributes = { [roofLevel.id]: { windowHeadHeight: 0 } };

  // create a dummy block for the roof walls
  const roofBlock = makeRoofBlock(roofLevel);
  const roofBlocks = { [roofBlock.id]: roofBlock };

  // create roof perimeter planar graph
  const planarGraph = polygonPlanarGraphNew(roofWallPerimeters.map(({ perimeter }) => perimeter));
  const planarGraphs = { [roofLevel.id]: planarGraph };
  const allPerimeters = planarGraphTracePerimeter(planarGraph.planarGraph);

  const planarGraphLevelAssociations = makePlanarGraphLevelAssociations(roofLevels, planarGraphs);

  // create a dummy rooms for the roof walls
  const { rooms, roomArray, roomLevelIndices, roomsByLevel } = makeRoofRooms({
    roofLevels,
    roofLevel,
    roofBlock,
    roofWallPerimeters,
    roofWallMode,
  });

  // room connections
  const roomConnections = makeRoofRoomsConnections({
    roofLevel,
    rooms,
    roomArray,
    roomLevelIndices,
    roomsByLevel,
    planarGraphs,
    roofWallPerimeters,
  });

  // create a lookup for inside corner half edges
  const insideCornerHalfEdges = getInsideCornerHalfEdges({
    planarGraph: planarGraph.planarGraph,
    coveredPerimeters: allPerimeters,
  });

  const insideCornerHalfEdgeLookup: { [halfEdge: number]: boolean } = {};
  for (const halfEdge of insideCornerHalfEdges) {
    insideCornerHalfEdgeLookup[halfEdge] = true;
  }

  // half edge associations
  const roofLevelLabels = makeRoofLabelHalfEdgeMaps(
    roomArray,
    planarGraph,
    roofWallPerimeters,
    gableEndLabels,
    peakDirection,
    mirror,
    rootInstanceId,
  );

  // reorder facade spans for old roof facades
  const sortedFacadeSpanStratum: RWTable<FacadeSpanStratum> = {};
  for (const { facadeId: rawFacadeId, face } of gableEndLabels) {
    const lookupFacadeId = resolveFacadeId(rawFacadeId);
    const facade = facades[lookupFacadeId];
    if (!facade) {
      continue;
    }

    const spans: FacadeSpanStratum[] = [];
    docEntityGetSortedChildrenFromTable(facadeSpanStratum, lookupFacadeId).forEach((spanStrata) => {
      const validUpperDatum = isSpanStrataDatumValidForRoof(spanStrata.upperDatumUsage, face);
      const validLowerDatum = isSpanStrataDatumValidForRoof(spanStrata.lowerDatumUsage, face);

      if (validUpperDatum) {
        spans.push(spanStrata);
      }

      if (!validUpperDatum || !validLowerDatum) {
        addIssuesForInvalidRoofSpanStrata(spanStrata, face, rawFacadeId);
      }
    });

    const sortedSpans = facade?.legacyRoofBehavior
      ? fixLegacyRoofFacadeSpanOrder(spans, mutableDatumAbstractionsOut, level)
      : spans;

    for (let i = 0, l = sortedSpans.length; i < l; i++) {
      const span = sortedSpans[i];
      sortedFacadeSpanStratum[span.id] = {
        ...span,
        sortIndex: i,
      };
    }
  }

  // generate wall paths for roof wall system
  const wallPaths = makeWallPathsForLevel({
    levelId: roofLevel.id,
    levels: roofLevels,
    docRooms,
    rooms,
    roomsByLevel,
    roomLevelIndices,
    roomConnections,
    roomFloorAssemblies: {},
    planarGraph,
    planarGraphLevelAssociations: planarGraphLevelAssociations[roofLevel.id],
    wallSettings,
    foundationSettings,
    foundationComponents,
    foundationAssemblies,
    foundationAssemblyOverrides: {},
    foundationPatterns: {},
    framingComponents,
    slabComponents,
    sheathingComponents,
    datumAbstractions: mutableDatumAbstractionsOut,
    facades,
    levelLabels: roofLevelLabels,
    facadeSpanStratum: sortedFacadeSpanStratum,
    facadeSweepStratum,
    wallAssemblyOverrides: {},
    blocks: roofBlocks,
    roofLevelPerimeters: [],
  });

  const wallDatumAbstractionProvider = new WallDatumAbstractionProvider(
    roofLevels,
    roofLevelAttributes,
    mutableDatumAbstractionsOut,
  );

  // build roof wall mass system
  const wallMassSystem = makeWallMassSystem({
    rooms: roomArray,
    wallSettings,
    planarGraph,
    wallCatalogSettings,
    roomLevelIndices,
    roomConnections,
    levelId: roofLevel.id,
    levels: roofLevels,
    framingComponents,
    slabComponents,
    sheathingComponents,
    insideCornerHalfEdgeLookup,
    foundationComponents,
    wallDatumAbstractionProvider,
    mirror,
    wallPaths,
  });

  const { halfEdgeToWallCutSet, extraWallCutBSPs } = makeRoofWallCutHalfEdgeMap(planarGraph, roofLevel, roofWallCuts);

  const wallMasses: Table<WallMassSystem> = { [roofLevel.id]: wallMassSystem };
  let wallSystem = makeWallSystem({
    wallDatumAbstractionProvider,
    wallMasses,
    halfEdgeToWallCutSet,
    levelId: roofLevel.id,
    wallCatalogSettings,
    mirror,
    issues,
  });

  // clip walls
  wallSystem = clipRoofWallSystem({
    wallSystem,
    roofLevel,
    roof,
    roofWallPerimeters,
    planarGraph,
    upperFloorsRoomsBSPs,
    wallOverlapBSPs,
    halfStoriesBSP,
    roofWallDiscardBSP,
    extraWallCutBSPs,
    clipContext,
    fillGaps,
  });

  // corner board info
  makeUnderRoofCornerTrims({
    level,
    roof,
    planarGraph: planarGraph.planarGraph,
    wallSystem,
    facades,
    labels: roofLevelLabels,
    underRoofTrim,
    datumsAtCorner,
    exteriorTrimSettings,
    profileSource,
    issues,
  });

  const edges = makeRoofWallSystemEdgeMap({
    roof,
    wallSystem,
    planarGraph,
    roofWallPerimeters,
  });

  /**
   * @NOTE - we must update the mutableDatumAbstractionsOut table with the new datum abstractions
   * that were generated during the roof wall system generation process.
   *
   * @TODO - maybe there should be a flag on the provider to indicate that the table should
   * be mutable or immutable.
   *  - if mutable, we don't need to update the table here, since adding
   * a new datum abstraction to the provider will automatically update the table.
   *  - if immutable, we need to update the table here.
   */
  const updatedDatumAbstractions = wallDatumAbstractionProvider.getDatumAbstractions();
  for (const datumId in updatedDatumAbstractions) {
    mutableDatumAbstractionsOut[datumId] = updatedDatumAbstractions[datumId];
  }

  const planarGraphPerimeterInfo = getRoofPlanarGraphPerimeterInfo(planarGraph, mirror);

  return {
    wallMassSystem,
    wallSystem,
    planarGraph,
    rooms,
    levels: roofLevels,
    levelsAttributes: roofLevelAttributes,
    levelId: roofLevel.id,
    edges,
    facadeTrimOffsetZ,
    planarGraphPerimeterInfo,
  };
}

export function fixLegacyRoofFacadeSpanOrder(
  facadeSpans: FacadeSpanStratum[],
  datumAbstractions: Table<DatumAbstraction>,
  roofLevel: Level,
): FacadeSpanStratum[] {
  //
  const makeWallDatums = (level: Level): WallSideGeneratedDatums => {
    const { levelDatumsGenerated, topOfWall } = level;
    const { roofBottom, roofTop } = levelDatumsGenerated;

    return {
      foundationBottom: levelInchesNew(0),
      foundationTop: levelInchesNew(0),
      floorBottom: levelInchesNew(0),
      floorTop: levelInchesNew(0),
      ceilingBottom: topOfWall,
      ceilingTop: topOfWall,
      wallTop: topOfWall,
      roofBottom,
      roofTop,
    };
  };

  const wallSideDatums = makeWallDatums(roofLevel);

  // get facade datums
  const wallFacadeDatums = collectWallFacadeDatums(datumAbstractions, facadeSpans, []);

  // reorder
  let currLowerDatumOffset = Number.NEGATIVE_INFINITY;
  let currUpperDatumOffset = Number.POSITIVE_INFINITY;

  const sortedFacadeSpans: FacadeSpanStratum[] = [];
  let overlappingFacadeSpans: FacadeSpanStratum[] = [];
  for (const span of facadeSpans) {
    const lowerDatum =
      wallAssemblyVerticalDatumFromFacade(wallFacadeDatums[span.lowerDatumUsage.entityId]) ??
      defaultWallDatumAbstractions.lowerDatum;
    const upperDatum =
      wallAssemblyVerticalDatumFromFacade(wallFacadeDatums[span.upperDatumUsage.entityId]) ??
      defaultWallDatumAbstractions.upperDatum;

    const lowerDatumOffset = lowerDatum.resolveVerticalDatumRelativeToLevel({
      level: roofLevel,
      wallSide: { wallSideDatums },
    });
    const upperDatumOffset = upperDatum.resolveVerticalDatumRelativeToLevel({
      level: roofLevel,
      wallSide: { wallSideDatums },
    });

    if (currLowerDatumOffset < lowerDatumOffset || currUpperDatumOffset > upperDatumOffset) {
      sortedFacadeSpans.push(...overlappingFacadeSpans.reverse());
      overlappingFacadeSpans = [];
    }

    overlappingFacadeSpans.push(span);

    currLowerDatumOffset = lowerDatumOffset;
    currUpperDatumOffset = upperDatumOffset;
  }

  sortedFacadeSpans.push(...overlappingFacadeSpans.reverse());

  return sortedFacadeSpans;
}

interface PlanarGraphCornerBox extends AABB2 {
  readonly edge1: number;
  readonly edge2: number;
}

function buildPlanarGraphCornersRTree(planarGraph: PlanarGraph, wallSystem: WallSystem): RBush<PlanarGraphCornerBox> {
  const { halfEdgeToWall, walls } = wallSystem;
  const { faceHalfEdges } = planarGraphTracePerimeter(planarGraph);

  const rtree = new RBush<PlanarGraphCornerBox>();
  for (let face = 0, faceCount = faceHalfEdges.length; face < faceCount; face++) {
    for (
      let halfEdgeIndex = 0, halfEdgeCount = faceHalfEdges[face].length;
      halfEdgeIndex < halfEdgeCount;
      halfEdgeIndex++
    ) {
      const edge1 = faceHalfEdges[face][halfEdgeIndex];
      const edge2 = faceHalfEdges[face][(halfEdgeIndex + 1) % halfEdgeCount];

      const wall1 = walls[halfEdgeToWall[edge1]];
      const wall2 = walls[halfEdgeToWall[edge2]];

      if (!wall1 || wall1.wallType !== WallType.SOLID || !wall2 || wall2.wallType !== WallType.SOLID) {
        continue;
      }

      const [x, y] = planarGraphHalfEdgeEnd(planarGraph, edge1);

      rtree.insert({
        minX: x - CORNER_BOARD_COLLISION_TOLERANCE,
        minY: y - CORNER_BOARD_COLLISION_TOLERANCE,
        maxX: x + CORNER_BOARD_COLLISION_TOLERANCE,
        maxY: y + CORNER_BOARD_COLLISION_TOLERANCE,
        edge1,
        edge2,
      });
    }
  }

  return rtree;
}

function getNonLegacyRoofCornerBoardFacades(facades: Table<Facade>): Table<Facade> {
  return Object.entries(facades).reduce((acc, [facadeId, facade]) => {
    if (!facade.legacyRoofCornerBoardBehavior) {
      acc[facadeId] = facade;
    }

    return acc;
  }, {});
}

interface MakeUnderRoofCornerTrimsOptions {
  readonly level: Level;
  readonly roof: GeneratedRoof;
  readonly planarGraph: PlanarGraph;
  readonly wallSystem: WallSystem;
  readonly facades: Table<Facade>;
  readonly labels: HalfEdgeAssociationTable<Label>;
  readonly underRoofTrim: UnderRoofTrim[];
  readonly datumsAtCorner: RBush<CornerBoardDatumsAABB>;
  readonly exteriorTrimSettings: ExteriorTrimSettings;
  readonly profileSource: ActiveProfileSource;
  readonly issues: BuildingGenerationIssue[];
}

function makeUnderRoofCornerTrims({
  level,
  roof,
  planarGraph,
  wallSystem,
  facades,
  labels,
  underRoofTrim,
  datumsAtCorner,
  exteriorTrimSettings,
  profileSource,
  issues,
}: MakeUnderRoofCornerTrimsOptions) {
  const { id: levelId } = level;
  const { roofVolume, roofSettings, rails, srcRoofPerimeterRails, roofTopFaces } = roof;
  const { baseHeight, heelHeightOffsets } = roofVolume;
  const { peakRail } = rails;
  const wallBaseHeight = baseHeight - heelHeightOffsets.refOffset;
  const nonLegacyFacadesTable = getNonLegacyRoofCornerBoardFacades(facades);

  const {
    outsideCornerBoardTopAlignment = CornerBoardTrimAlignment.BO_SOFFIT,
    outsideCornerBoardProfile = ProfileExtrusionType.DEFAULT_PROFILE,
    insideCornerBoardProfile = ProfileExtrusionType.DEFAULT_PROFILE,
  } = exteriorTrimSettings;

  const {
    eaveType,
    eaveFriezeBoard,
    soffitOffset = 0,
    friezeBoardOrientation = StreetOrientationMode.FRONT_AND_SIDE_FACES,
  } = roofSettings;

  const outSideDescriptor = getActiveProfileDescriptor(
    profileSource,
    outsideCornerBoardProfile,
    DEFAULT_CORNER_TRIM_PROFILE_DESCRIPTOR,
  );
  const exteriorTrimHandle: DocEntityHandle | undefined = exteriorTrimSettings.id
    ? { type: "SETTINGS", id: exteriorTrimSettings.id }
    : undefined;
  if (!outSideDescriptor) {
    pushMissingProfileReferenceIssue(
      issues,
      profileSource,
      outsideCornerBoardProfile,
      exteriorTrimHandle,
      "outsideCornerBoardProfile",
    );
  }

  const outsideWidth = outSideDescriptor
    ? aabb2Height(polyLine2AABB2(getProfileExtrusionDescriptorProfile(outSideDescriptor)))
    : 0;

  const insideDescriptor = getActiveProfileDescriptor(
    profileSource,
    insideCornerBoardProfile,
    DEFAULT_CORNER_TRIM_PROFILE_DESCRIPTOR,
  );
  if (!insideDescriptor) {
    pushMissingProfileReferenceIssue(
      issues,
      profileSource,
      insideCornerBoardProfile,
      exteriorTrimHandle,
      "insideCornerBoardProfile",
    );
  }

  const insideWidth = insideDescriptor
    ? aabb2Height(polyLine2AABB2(getProfileExtrusionDescriptorProfile(insideDescriptor)))
    : 0;

  const rtree = buildRoofPerimeterRailsRTree(srcRoofPerimeterRails);

  // This planar graph is used for translating the roof corners into half edges, so we can get
  // the facades applied to the incoming and outgoing edges of that roof corner.
  const planarGraphCornersRTree = buildPlanarGraphCornersRTree(planarGraph, wallSystem);

  // helper function for adding the corner datums
  const addDatums = (x: number, y: number, bottomOfSoffit: number, bottomOfFriezeBoard: number) => {
    const aabb = aabb2New(
      x - ROOF_CORNER_BOARD_TOLERANCE,
      y - ROOF_CORNER_BOARD_TOLERANCE,
      x + ROOF_CORNER_BOARD_TOLERANCE,
      y + ROOF_CORNER_BOARD_TOLERANCE,
    );

    datumsAtCorner.insert({
      ...aabb,
      bottomOfSoffit,
      bottomOfFriezeBoard,
      levelId,
    });
  };

  // helper function for adding the corner points
  const addCornerPoints = (
    roofPerimeterRail: RoofPerimeterRail,
    wallPoints: PolyLine2,
    edgePlane: Plane,
    refX: number,
    refY: number,
    wallCenterCornerX: number,
    wallCenterCornerY: number,
    isEnd: boolean,
  ) => {
    const { prevPerimeterRail, nextPerimeterRail } = roofPerimeterRail;

    const cornerCondition = getCornerCondition(
      wallPoints[4],
      wallPoints[5],
      wallPoints[2],
      wallPoints[3],
      wallPoints[0],
      wallPoints[1],
    );

    if (!isValidCornerCondition(cornerCondition)) {
      return;
    }

    const isInsideCorner = cornerCondition === ValidCornerCondition.INSIDE;
    const width = isInsideCorner ? insideWidth : outsideWidth;
    const dist = -width + 3;

    let cornerMouldingHeight = baseHeight + Math.max(soffitOffset, 0);

    const isLegacyCornerBoard = outsideCornerBoardTopAlignment === CornerBoardTrimAlignment.BO_SOFFIT_LEGACY;
    const hasSoffit = isLegacyCornerBoard ? !isOpenEave[eaveType] : hasEaveSoffit[eaveType];

    if (isInsideCorner) {
      cornerMouldingHeight += Math.max(
        getRoofHeightAtPoint(roofTopFaces, refX, refY, baseHeight) - baseHeight - GABLE_ROOF_THICKNESS,
        0,
      );
    } else {
      cornerMouldingHeight += getRoofHeightAtPoint(roofTopFaces, refX, refY, baseHeight) - baseHeight;
    }

    if (hasSoffit) {
      const perimSoffitRail = buildEnclosingRail(roofPerimeterRail, false);
      if (isRoofTrimRailTypeSoffit(perimSoffitRail)) {
        const { innerPlane, innerBottomRail } = perimSoffitRail;
        const bottomSoffitRail = polyLine2OnPlane(innerPlane, innerBottomRail);
        [, , cornerMouldingHeight] = isEnd
          ? polyLine3EndPoint(bottomSoffitRail)
          : polyLine3StartPoint(bottomSoffitRail);
      }

      if (!isEnd && prevPerimeterRail) {
        const prevPerimSoffitRail = buildEnclosingRail(prevPerimeterRail, false);
        if (isRoofTrimRailTypeSoffit(prevPerimSoffitRail)) {
          const { innerPlane: prevInnerPlane, innerBottomRail: prevInnerBottomRail } = prevPerimSoffitRail;
          const prevBottomSoffitRail = polyLine2OnPlane(prevInnerPlane, prevInnerBottomRail);
          const [, , prevCornerMouldingHeight] = polyLine3EndPoint(prevBottomSoffitRail);

          cornerMouldingHeight = Math.max(cornerMouldingHeight, prevCornerMouldingHeight);
        }
      }

      if (isEnd && nextPerimeterRail) {
        const nextPerimSoffitRail = buildEnclosingRail(nextPerimeterRail, false);
        if (isRoofTrimRailTypeSoffit(nextPerimSoffitRail)) {
          const { innerPlane: nextInnerPlane, innerBottomRail: nextInnerBottomRail } = nextPerimSoffitRail;
          const nextBottomSoffitRail = polyLine2OnPlane(nextInnerPlane, nextInnerBottomRail);
          const [, , nextCornerMouldingHeight] = polyLine3StartPoint(nextBottomSoffitRail);

          cornerMouldingHeight = Math.max(cornerMouldingHeight, nextCornerMouldingHeight);
        }
      }
    } else if (!isLegacyCornerBoard && eaveType !== EaveType.ANGLED_ENCLOSED_OPEN) {
      cornerMouldingHeight -= GABLE_ROOF_THICKNESS;
    }

    let friezeBoardSize = 0;
    if (hasValidFriezeBoards(eaveFriezeBoard) && eaveType !== EaveType.ANGLED_OPEN_OPEN) {
      if (includeElementBasedOnOrientation(edgePlane, friezeBoardOrientation)) {
        for (const { friezeBoard, verticalOffset = 0 } of eaveFriezeBoard || []) {
          const descriptor = getActiveProfileDescriptor(profileSource, friezeBoard, DEFAULT_TRIM_PROFILE_DESCRIPTOR);
          if (!descriptor) {
            pushMissingProfileReferenceIssue(
              issues,
              profileSource,
              friezeBoard,
              { type: "ROOF", id: roof.id },
              "eaveFriezeBoard",
            );
            continue;
          }
          const profile = getProfileExtrusionDescriptorProfile(descriptor);
          const friezeBoardProfile = polyLine2Simplify(polyLine2Copy(profile));
          const friezeBoardProfileAABB = polyLine2AABB2(friezeBoardProfile);
          friezeBoardSize = Math.max(friezeBoardSize, aabb2Height(friezeBoardProfileAABB) + verticalOffset);
        }
      }
    }

    if (!isLegacyCornerBoard) {
      addDatums(wallPoints[2], wallPoints[3], cornerMouldingHeight, cornerMouldingHeight - friezeBoardSize);
    }

    const [x, y] = polyLine2MiterPt(
      wallPoints[0],
      wallPoints[1],
      wallPoints[2],
      wallPoints[3],
      wallPoints[4],
      wallPoints[5],
      dist,
    );
    const roofSide = line2Side(peakRail[0], peakRail[1], peakRail[3], peakRail[4], x, y);
    const sideHeelHeightOffset =
      roofSide === Line2Side.RIGHT ? heelHeightOffsets.rightOffset : heelHeightOffsets.leftOffset;
    const hasHeelHeightOffset = heelHeightOffsets.refOffset + sideHeelHeightOffset > DIST_EPSILON;
    if (cornerMouldingHeight - wallBaseHeight > DIST_EPSILON) {
      const trimBase: Vec3 = [x, y, wallBaseHeight];
      const trimTop: Vec3 = [x, y, cornerMouldingHeight];
      const wallCenterCorner: Vec2 = [wallCenterCornerX, wallCenterCornerY];
      const isDuplicate = underRoofTrim.some(
        (trim) =>
          vec3DistSquared(trimBase[0], trimBase[1], trimBase[2], trim.trimBase[0], trim.trimBase[1], trim.trimBase[2]) <
            DIST_EPSILON_SQUARED &&
          vec3DistSquared(trimTop[0], trimTop[1], trimTop[2], trim.trimTop[0], trim.trimTop[1], trim.trimTop[2]) <
            DIST_EPSILON_SQUARED,
      );
      if (!isDuplicate) {
        const planarGraphCorners = planarGraphCornersRTree.search({
          minX: wallCenterCornerX - CORNER_BOARD_COLLISION_TOLERANCE,
          minY: wallCenterCornerY - CORNER_BOARD_COLLISION_TOLERANCE,
          maxX: wallCenterCornerX + CORNER_BOARD_COLLISION_TOLERANCE,
          maxY: wallCenterCornerY + CORNER_BOARD_COLLISION_TOLERANCE,
        });

        const firstPlanarGraphCorner = planarGraphCorners.length > 0 ? planarGraphCorners[0] : null;

        const config = firstPlanarGraphCorner
          ? cornerBoardConfigForEdge(
              nonLegacyFacadesTable,
              labels,
              firstPlanarGraphCorner.edge1,
              firstPlanarGraphCorner.edge2,
            )
          : emptyCornerBoardConfig();

        underRoofTrim.push({
          trimBase,
          trimTop,
          wallPoints,
          levelId,
          hasHeelHeightOffset,
          cornerCondition,
          wallCenterCorner,
          config,
        });
      }
    }
  };

  for (const roofPerimeterRail of srcRoofPerimeterRails) {
    const { rail, edgePlane } = roofPerimeterRail;
    const {
      rail: wallRail,
      railWithCorners: wallRailWithCorners,
      startCornerType,
      endCornerType,
    } = getRoofPerimeterWallRail({ roofPerimeterRail, rtree, roofTopFaces });

    const { rail: wallCenterRail } = getRoofPerimeterWallRail({
      roofPerimeterRail,
      rtree,
      roofTopFaces,
      getExteriorOffset: () => 0,
    });

    const hasStartCorner =
      startCornerType === RoofRailCornerType.INSIDE || startCornerType === RoofRailCornerType.OUTSIDE;
    const hasEndCorner = endCornerType === RoofRailCornerType.INSIDE || endCornerType === RoofRailCornerType.OUTSIDE;

    if (!wallRail || !wallCenterRail) {
      continue;
    }

    if (polyLine3Length(wallRail) <= 2 * DIST_EPSILON) {
      continue;
    }

    // add the under roof trims at corners
    if (hasStartCorner) {
      const [refX, refY] = polyLine3StartPoint(rail);
      const [wallCenterCornerX, wallCenterCornerY] = polyLine3StartPoint(wallCenterRail);
      const startCorner = polyLine2New([
        wallRailWithCorners[0],
        wallRailWithCorners[1],
        wallRailWithCorners[3],
        wallRailWithCorners[4],
        wallRailWithCorners[6],
        wallRailWithCorners[7],
      ]);

      addCornerPoints(
        roofPerimeterRail,
        startCorner,
        edgePlane,
        refX,
        refY,
        wallCenterCornerX,
        wallCenterCornerY,
        false,
      );
    }

    if (hasEndCorner) {
      const [refX, refY] = polyLine3EndPoint(rail);
      const [wallCenterCornerX, wallCenterCornerY] = polyLine3EndPoint(wallCenterRail);
      const l = wallRailWithCorners.length;
      const endCorner = polyLine2New([
        wallRailWithCorners[l - 9],
        wallRailWithCorners[l - 8],
        wallRailWithCorners[l - 6],
        wallRailWithCorners[l - 5],
        wallRailWithCorners[l - 3],
        wallRailWithCorners[l - 2],
      ]);

      addCornerPoints(roofPerimeterRail, endCorner, edgePlane, refX, refY, wallCenterCornerX, wallCenterCornerY, true);
    }
  }
}
